import classNames from "classnames";
import { CSSProperties, ReactNode } from "react";

import css from "./typography.module.scss";
import { VariantBody, VariantHeader, VariantLabel } from "./typography.types";
import { VARIANTS } from "./typography.utils";

export type TextOverflow = "break" | "clip" | "wrap" | false;

type CommonProps = {
  id?: string;
  className?: string;
  withMargin?: boolean;
  qaId?: string;
  title?: string;
  style?: CSSProperties;
  placeholder?: boolean;
};

type OverflowProps = {
  textOverflow?: TextOverflow;
};

type ConditionalVariantBody =
  | {
      variant: VariantBody;
      tag?: "a";
      href?: string;
      htmlFor?: string;
    }
  | {
      variant: VariantBody;
      tag?: "span" | "p" | "div";
      href?: never;
      htmlFor?: string;
    };

type ConditionalVariantLabel =
  | {
      variant: VariantLabel;
      tag?: "a";
      href?: string;
      htmlFor?: string;
    }
  | {
      variant: VariantLabel;
      tag?: "span" | "p" | "label";
      href?: never;
      htmlFor?: string;
    };

type ConditionalProps =
  | ConditionalVariantBody
  | ConditionalVariantLabel
  | {
      variant: VariantHeader;
      tag?: never;
      href?: never;
      htmlFor?: never;
    };

type TitleConstraint<P> = [P] extends [{ textOverflow: "clip" }]
  ? [P] extends [{ children: string }]
    ? { title?: string }
    : { title: string }
  : { title?: string };

export type TypographyProps = CommonProps & ConditionalProps & OverflowProps & { children?: ReactNode };

export const Typography = <P extends OverflowProps & { children?: ReactNode }>({
  id,
  variant,
  tag,
  href,
  htmlFor,
  className,
  withMargin = false,
  qaId,
  textOverflow,
  title,
  style,
  placeholder = false,
  children
}: CommonProps & ConditionalProps & OverflowProps & { children?: ReactNode } & P & TitleConstraint<P>) => {
  const { variantTag, className: variantClassName } = VARIANTS[variant];
  const Element = tag ?? variantTag;

  const clipOverflow = textOverflow === "clip";
  const defaultTitle = typeof children === "string" && clipOverflow ? children : undefined;
  const resolvedTitle = title ?? defaultTitle;

  return (
    <Element
      id={id}
      className={classNames(
        variantClassName,
        {
          [css.noMargin]: !withMargin,
          [css.clipOverflow]: clipOverflow,
          [css.wrap]: textOverflow === "wrap",
          [css.breakWords]: textOverflow === "break",
          [css.placeholder]: placeholder
        },
        className
      )}
      href={href}
      htmlFor={htmlFor}
      data-qa-id={qaId}
      title={resolvedTitle}
      style={style}
    >
      {children}
    </Element>
  );
};
